import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  X,
  CircleCheck as CheckCircle,
  CircleX as XCircle,
  Shield,
  Phone,
  MessageSquare,
  Monitor,
  Clock,
  Video,
  FileText,
  TriangleAlert as AlertTriangle,
  LifeBuoy
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

import ContactForm from '../ContactForm';

// Header Component (Reused locally to avoid prop drilling if not global)
const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('soporte.header_cta')}
        </button>
      </div>
    </div>
  </header>
);

const ChannelCard = ({ icon: Icon, title, description, colorClass }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col items-start h-full">
    <div className={`p-3 rounded-lg mb-4 ${colorClass.bg} ${colorClass.text}`}>
      <Icon size={24} />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const SlaCard = ({ title, tickets, hav, responseTime, hours, colorClass, recommended = false, onConsult, t }) => (
  <div className={`relative bg-white border ${recommended ? 'border-blue-500 shadow-xl scale-105 z-10' : 'border-gray-200'} rounded-xl p-6 flex flex-col h-full w-full`}>
    {recommended && (
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
        {t('soporte.plan_labels.popular')}
      </div>
    )}
    <div className={`text-center mb-6`}>
      <h3 className={`text-2xl font-bold ${colorClass}`}>{title}</h3>
      <div className="mt-2 text-sm text-gray-500 font-medium">
        {hours}
      </div>
    </div>
    <ul className="space-y-4 mb-6 flex-grow">
      <li className="flex items-center gap-3 text-sm text-gray-700">
        <MessageSquare size={16} className="text-gray-400" />
        <span><strong>{tickets}</strong> {t('soporte.plan_labels.tickets_year')}</span>
      </li>
      <li className="flex items-center gap-3 text-sm text-gray-700">
        <Video size={16} className="text-gray-400" />
        <span><strong>{hav}</strong> {t('soporte.plan_labels.video_call')}</span>
      </li>
      <li className="flex items-center gap-3 text-sm text-gray-700">
        <Clock size={16} className="text-gray-400" />
        <span>{t('soporte.plan_labels.response')}: <strong>{responseTime} {t('soporte.plan_labels.max')}</strong></span>
      </li>
    </ul>
    <button
      onClick={onConsult}
      className={`w-full py-2 rounded-lg font-semibold transition-colors ${recommended ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
    >
      {t('soporte.plan_labels.consult_plan')}
    </button>
  </div>
);

const ProcessStep = ({ number, title, description, icon: Icon }) => (
  <div className="flex flex-col md:flex-row items-center gap-6 p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
    <div className="flex-shrink-0 w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-bold text-xl relative">
      <Icon size={28} />
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#141414] text-white text-sm rounded-full flex items-center justify-center border-2 border-white">
        {number}
      </div>
    </div>
    <div className="text-center md:text-left">
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  </div>
);

const SoportePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isTprOpen, setIsTprOpen] = useState(false);
  const [tprSeries, setTprSeries] = useState(() => Array.from({ length: 24 }, () => 2.5 + Math.random() * 1.4));
  const [tprLastUpdatedAt, setTprLastUpdatedAt] = useState(() => new Date());

  const openPopup = () => {
    setIsTprOpen(false);
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = isTprOpen ? 'hidden' : 'auto';
  };

  const openTpr = () => {
    setIsPopupOpen(false);
    setIsTprOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeTpr = () => {
    setIsTprOpen(false);
    document.body.style.overflow = isPopupOpen ? 'hidden' : 'auto';
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTprSeries((prev) => {
        const last = prev[prev.length - 1] ?? 3;
        const noise = (Math.random() - 0.5) * 0.9;
        const drift = (3 - last) * 0.18;
        const next = Math.max(0.6, Math.min(12, last + drift + noise));
        return [...prev.slice(1), next];
      });
      setTprLastUpdatedAt(new Date());
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const tprCurrentHours = useMemo(() => {
    const sum = tprSeries.reduce((acc, v) => acc + v, 0);
    return tprSeries.length ? sum / tprSeries.length : 3;
  }, [tprSeries]);

  const slaMaxByPlan = useMemo(
    () => ([
      { label: 'Basic', hours: 12, color: 'bg-slate-200' },
      { label: 'Core', hours: 6, color: 'bg-blue-200' },
      { label: 'Advance', hours: 4, color: 'bg-indigo-200' },
      { label: 'Elite', hours: 2, color: 'bg-purple-200' },
      { label: 'Ultra', hours: 1, color: 'bg-gray-200' }
    ]),
    []
  );

  const tprLastFour = useMemo(() => {
    const points = tprSeries.slice(-4);
    if (points.length >= 4) return points;
    const paddingValue = points[0] ?? 3;
    return Array.from({ length: 4 - points.length }, () => paddingValue).concat(points);
  }, [tprSeries]);

  const tprSpark = useMemo(() => {
    const values = tprLastFour;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(0.2, max - min);
    const x = [4, 28, 52, 76];
    const y = values.map((v) => {
      const t = (v - min) / range;
      return 26 - t * 18;
    });
    const segments = [0, 1, 2].map((i) => {
      const isDown = values[i + 1] < values[i];
      const isUp = values[i + 1] > values[i];
      return {
        x1: x[i],
        y1: y[i],
        x2: x[i + 1],
        y2: y[i + 1],
        stroke: isDown ? 'rgba(52, 211, 153, 0.95)' : isUp ? 'rgba(248, 113, 113, 0.95)' : 'rgba(255, 255, 255, 0.35)'
      };
    });
    const lastDelta = values[3] - values[2];
    const lastDeltaLabel = `${lastDelta >= 0 ? '+' : ''}${lastDelta.toFixed(1)}h`;
    const lastDeltaColor = lastDelta < 0 ? 'text-emerald-200' : lastDelta > 0 ? 'text-red-200' : 'text-white/70';
    return { x, y, segments, lastDeltaLabel, lastDeltaColor };
  }, [tprLastFour]);

  const supportChannels = useMemo(() => {
    const channels = t('soporte.channels', { returnObjects: true });
    const icons = [Monitor, FileText, Phone];
    const colors = [
      { bg: 'bg-purple-100', text: 'text-purple-600' },
      { bg: 'bg-blue-100', text: 'text-blue-600' },
      { bg: 'bg-green-100', text: 'text-green-600' }
    ];
    return channels.map((channel, i) => ({ ...channel, icon: icons[i], colorClass: colors[i] }));
  }, [t]);

  const slaPlans = useMemo(() => t('soporte.plans', { returnObjects: true }), [t]);
  const processSteps = useMemo(() => t('soporte.process_steps', { returnObjects: true }), [t]);
  const includeItems = useMemo(() => t('soporte.scope_include.items', { returnObjects: true }), [t]);
  const excludeItems = useMemo(() => t('soporte.scope_exclude.items', { returnObjects: true }), [t]);
  const tprTimeLocale = useMemo(() => {
    if (i18n.language?.startsWith('pt')) return 'pt-BR';
    if (i18n.language?.startsWith('en')) return 'en-US';
    return 'es-ES';
  }, [i18n.language]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('soporte.popup.close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('soporte.popup.title')}</h3>
              <p className="text-gray-600 mb-6">{t('soporte.popup.desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      {isTprOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closeTpr(); }}>
          <div className="relative w-full max-w-5xl overflow-hidden rounded-3xl bg-[#141414] text-white border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/35 via-indigo-900/25 to-purple-900/35" />
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/20 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-purple-500/20 blur-3xl rounded-full" />

            <button
              onClick={closeTpr}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition z-10"
              aria-label={t('soporte.tpr.close')}
            >
              <X size={24} />
            </button>

            <div className="relative p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10 items-stretch">
                <div className="lg:col-span-2">
                  <h2 className="text-3xl font-bold mb-3">{t('soporte.tpr.title')}</h2>
                  <p className="text-white/80 max-w-2xl">
                    {t('soporte.tpr.desc')}
                  </p>
                </div>

                <div className="mt-10 lg:mt-0 h-full">
                  <div className="grid grid-cols-1 md:grid-cols-[4fr_5fr] gap-6 h-full">
                    <div className="grid grid-rows-2 gap-6 h-full">
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 h-full">
                        <div className="text-sm font-semibold text-white/70">{t('soporte.tpr.current_label')}</div>
                        <div className="mt-2 flex items-baseline gap-2">
                          <div className="text-6xl md:text-7xl font-extrabold tracking-tight leading-none">{tprCurrentHours.toFixed(1)}</div>
                          <div className="text-white/70 font-semibold text-lg md:text-xl">{t('soporte.tpr.hrs')}</div>
                        </div>
                        <div className="mt-3 text-xs text-white/60">
                          {t('soporte.tpr.last_update', {
                            time: tprLastUpdatedAt.toLocaleTimeString(tprTimeLocale, { hour: '2-digit', minute: '2-digit' })
                          })}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 h-full">
                        <div className="text-sm font-semibold text-white/70">{t('soporte.tpr.update_frequency')}</div>
                        <div className="mt-2 text-3xl font-extrabold tracking-tight">{t('soporte.tpr.update_frequency_val')}</div>
                        <div className="mt-3 text-xs text-white/60">
                          {t('soporte.tpr.update_note')}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 h-full flex flex-col">
                      <div className="text-sm font-semibold text-white/70">{t('soporte.tpr.plans_priority')}</div>
                      <div className="mt-3 space-y-3">
                        <div>
                          <div className="text-xs font-semibold text-white/60">{t('soporte.tpr.unaffected')}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <div className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/10 border border-white/10 text-white/85">
                              Ultra
                            </div>
                            <div className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/10 border border-white/10 text-white/85">
                              Elite
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-white/60">{t('soporte.tpr.applies_to')}</div>
                          <div className="mt-2 space-y-2">
                            {slaMaxByPlan
                              .filter((p) => p.label === 'Basic' || p.label === 'Core' || p.label === 'Advance')
                              .map((p) => {
                                const isAffected = tprCurrentHours > p.hours;
                                return (
                                  <div key={p.label} className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-semibold text-white/85">{p.label}</div>
                                    <div className="flex items-center gap-2">
                                      <div className="text-xs text-white/60">{t('soporte.tpr.sla_label', { hours: p.hours })}</div>
                                      <div className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${isAffected ? 'bg-red-500/15 border-red-500/25 text-red-200' : 'bg-emerald-500/15 border-emerald-500/25 text-emerald-200'}`}>
                                        {isAffected ? t('soporte.tpr.status_over') : t('soporte.tpr.status_under')}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                      <div className="mt-auto pt-6">
                        <div className="flex items-center justify-between gap-4">
                          <div className="text-xs font-semibold text-white/60">{t('soporte.tpr.last_3_hours')}</div>
                          <div className={`text-xs font-bold ${tprSpark.lastDeltaColor}`}>{tprSpark.lastDeltaLabel}</div>
                        </div>
                        <div className="mt-2">
                          <svg viewBox="0 0 80 32" className="w-full h-10">
                            <path d="M0 30H80" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
                            {tprSpark.segments.map((s, idx) => (
                              <line
                                key={idx}
                                x1={s.x1}
                                y1={s.y1}
                                x2={s.x2}
                                y2={s.y2}
                                stroke={s.stroke}
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              />
                            ))}
                            {tprSpark.x.map((cx, idx) => (
                              <circle
                                key={idx}
                                cx={cx}
                                cy={tprSpark.y[idx]}
                                r="2.4"
                                fill="rgba(255,255,255,0.85)"
                              />
                            ))}
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full h-full">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-6 h-full">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-sm font-semibold text-white/80">{t('soporte.tpr.trend_title')}</div>
                        <div className="text-xs text-white/60 mt-1">{t('soporte.tpr.trend_subtitle')}</div>
                      </div>
                      <div className="text-xs font-semibold px-3 py-1 rounded-full bg-teal-500/15 text-teal-200 border border-teal-500/20">
                        {t('soporte.tpr.live_badge')}
                      </div>
                    </div>

                    <div className="mt-6 h-28 flex items-end gap-1">
                      {tprSeries.map((v, idx) => {
                        const heightPct = Math.max(8, Math.min(100, (v / 12) * 100));
                        const isLast = idx === tprSeries.length - 1;
                        return (
                          <div
                            key={idx}
                            className={`flex-1 rounded-t-md ${isLast ? 'bg-indigo-400' : 'bg-white/15'}`}
                            style={{ height: `${heightPct}%` }}
                          />
                        );
                      })}
                    </div>

                    <div className="mt-6 space-y-3">
                      {slaMaxByPlan.map((p) => {
                        const maxWidthPct = (p.hours / 12) * 100;
                        const tprClamped = Math.min(tprCurrentHours, p.hours);
                        const tprWidthPct = (tprClamped / 12) * 100;
                        const isFocus = p.label === 'Basic' || p.label === 'Core' || p.label === 'Advance';
                        return (
                          <div key={p.label} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <div className={`font-semibold ${isFocus ? 'text-white' : 'text-white/70'}`}>{p.label}</div>
                              <div className="text-white/60">{t('soporte.tpr.sla_label', { hours: p.hours })}</div>
                            </div>
                            <div className="h-2 rounded-full bg-white/10 overflow-hidden relative">
                              <div className={`absolute inset-y-0 left-0 ${p.color}`} style={{ width: `${maxWidthPct}%` }} />
                              <div className="absolute inset-y-0 left-0 bg-indigo-400" style={{ width: `${tprWidthPct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-5 text-xs text-white/60">
                      {t('soporte.tpr.trend_footer')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} t={t} />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('soporte.back')}
          </button>

          {/* HERO */}
          <div className="text-center mb-20">
            <div className="inline-block bg-indigo-50 text-indigo-600 p-3 rounded-full mb-4">
              <LifeBuoy size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-6">
              {t('soporte.hero_title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 font-medium">
              {t('soporte.hero_subtitle')}
            </p>
            <p className="text-gray-500 max-w-4xl mx-auto leading-relaxed">
              {t('soporte.hero_desc')}
            </p>
          </div>

          {/* SECTION 1: Canales de Atención */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('soporte.channels_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('soporte.channels_subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {supportChannels.map((channel) => (
                <ChannelCard
                  key={channel.title}
                  icon={channel.icon}
                  title={channel.title}
                  description={channel.desc}
                  colorClass={channel.colorClass}
                />
              ))}
            </div>
          </section>

          {/* SECTION 2: SLA Levels */}
          <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('soporte.sla_title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('soporte.sla_subtitle')}
            </p>
            
            <div className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 items-stretch">
                {slaPlans.map((plan, i) => {
                  const colorClass = ['text-gray-600', 'text-blue-500', 'text-indigo-600', 'text-purple-600', 'text-[#141414]'][i] ?? 'text-gray-700';
                  return (
                    <SlaCard
                      key={plan.title}
                      title={plan.title}
                      tickets={plan.tickets}
                      hav={plan.hav}
                      responseTime={plan.response}
                      hours={plan.hours}
                      colorClass={colorClass}
                      recommended={Boolean(plan.recommended)}
                      onConsult={openPopup}
                      t={t}
                    />
                  );
                })}
              </div>
            </div>
            <div className="mt-10 flex justify-center">
              <button
                onClick={openTpr}
                className="w-full max-w-xl bg-[#141414] hover:bg-gray-800 text-white font-bold py-4 px-10 rounded-full transition shadow-lg"
              >
                {t('soporte.cta_tpr')}
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-6 text-center max-w-4xl mx-auto italic">
              {t('soporte.hav_note')}
            </p>
          </section>

          {/* SECTION 4: Resolution Process */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('soporte.process_title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <ProcessStep 
                number="1"
                icon={AlertTriangle}
                title={processSteps[0]?.title}
                description={processSteps[0]?.desc}
              />
              <ProcessStep 
                number="2"
                icon={Video}
                title={processSteps[1]?.title}
                description={processSteps[1]?.desc}
              />
            </div>
          </section>

          {/* SECTION 5: Scope */}
          <section className="mb-24">
             <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('soporte.scope_title')}</h2>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
               <div className="bg-green-50 border border-green-100 rounded-2xl p-8">
                 <div className="flex items-center gap-3 mb-6">
                   <CheckCircle className="text-green-600" size={32} />
                   <h3 className="text-2xl font-bold text-green-800">{t('soporte.scope_include.title')}</h3>
                 </div>
                 <ul className="space-y-4">
                   {includeItems.map((item) => (
                     <li key={item} className="flex items-start gap-3">
                       <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2"></span>
                       <span className="text-gray-700">{item}</span>
                     </li>
                   ))}
                 </ul>
               </div>

               <div className="bg-red-50 border border-red-100 rounded-2xl p-8">
                 <div className="flex items-center gap-3 mb-6">
                   <XCircle className="text-red-600" size={32} />
                   <h3 className="text-2xl font-bold text-red-800">{t('soporte.scope_exclude.title')}</h3>
                 </div>
                 <ul className="space-y-4">
                   {excludeItems.map((item) => (
                     <li key={item} className="flex items-start gap-3">
                       <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2"></span>
                       <span className="text-gray-700">{item}</span>
                     </li>
                   ))}
                 </ul>
                 <p className="text-xs text-red-500 mt-6 pt-4 border-t border-red-100">
                   {t('soporte.scope_note')}
                 </p>
               </div>
             </div>
          </section>

          {/* CTA FINAL */}
          <section className="text-center bg-[#141414] rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-900/20 to-purple-900/20 z-0"></div>
            <div className="relative z-10">
              <Shield size={48} className="mx-auto mb-6 text-blue-400" />
              <h2 className="text-3xl font-bold mb-6">{t('soporte.final_cta_title')}</h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">
                {t('soporte.final_cta_desc')}
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-4">
                <button
                  onClick={openPopup}
                  className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('soporte.final_cta_btn')}
                </button>
                <a 
                  href="https://xujvf.nextcoders.work/web/site/login" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-transparent border border-white text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('soporte.final_cta_teleport')}
                </a>
              </div>
              <p className="text-sm text-gray-400 mt-8 max-w-2xl mx-auto">
                {t('soporte.final_footer_quote')}
              </p>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default SoportePage;

